/**
 * @created     2026-11-03
 * @description CN-01 portal feedback tile and popup (internal only, not released).
 *              Submits once per session via POST /api/feedback; never via tracking or session JSON.
 *              Contract: docs/ARCHITECTURE.md "Portal feedback (CN-01, internal only, not released)".
 */
import { ChangeDetectionStrategy, Component, EventEmitter, Output, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ChatService } from '../../../core/services/chat.service';
import { I18nService } from '../../../core/services/i18n.service';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

const FEEDBACK_OUTCOMES = ['yes', 'partly', 'no'] as const;
type FeedbackOutcome = typeof FEEDBACK_OUTCOMES[number];

@Component({
  selector: 'app-feedback',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackComponent {
  private readonly http = inject(HttpClient);
  private readonly chatService = inject(ChatService);
  private readonly i18n = inject(I18nService);

  @Output() readonly reloginRequired = new EventEmitter<string | undefined>();

  readonly outcomes = FEEDBACK_OUTCOMES;
  readonly topics = ['payment', 'installments', 'document_upload', 'claim_question', 'other'] as const;
  readonly problems = ['bot_misunderstood', 'missing_information', 'technical_problem', 'other'] as const;
  readonly commentMaxLength = 1000;

  readonly open = signal(false);
  readonly outcome = signal<FeedbackOutcome | null>(null);
  readonly selectedTopics = signal<ReadonlySet<string>>(new Set());
  readonly selectedProblems = signal<ReadonlySet<string>>(new Set());
  readonly comment = signal('');
  readonly sending = signal(false);
  readonly done = signal<'submitted' | 'alreadySubmitted' | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly canSubmit = computed(() => this.outcome() !== null && !this.sending() && this.done() === null);

  toggleTopic(code: string): void {
    this.selectedTopics.update((set) => toggled(set, code));
  }

  toggleProblem(code: string): void {
    this.selectedProblems.update((set) => toggled(set, code));
  }

  submit(): void {
    const outcome = this.outcome();
    if (!this.canSubmit() || outcome === null) return;

    this.sending.set(true);
    this.errorMessage.set(null);
    this.http.post('/api/feedback', {
      session_id: this.chatService.getSessionId(),
      outcome,
      topics: this.topics.filter((code) => this.selectedTopics().has(code)),
      problems: this.problems.filter((code) => this.selectedProblems().has(code)),
      comment: this.comment(),
    }).subscribe({
      next: () => {
        this.sending.set(false);
        this.done.set('submitted');
      },
      error: (error: unknown) => {
        this.sending.set(false);
        const body = error instanceof HttpErrorResponse && error.error && typeof error.error === 'object'
          ? error.error as { code?: unknown; message?: unknown }
          : {};
        if (body.code === 'FEEDBACK_ALREADY_SUBMITTED') {
          this.done.set('alreadySubmitted');
          return;
        }
        if (body.code === 'NOT_AUTHENTICATED' || body.code === 'SESSION_REPLACED') {
          this.reloginRequired.emit(this.i18n.t(`chat.errors.${body.code}`));
          return;
        }
        this.errorMessage.set(body.code === 'VALIDATION_FAILED' && typeof body.message === 'string'
          ? body.message
          : this.i18n.t('chat.genericError'));
      },
    });
  }
}

function toggled(set: ReadonlySet<string>, code: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(code)) next.add(code);
  return next;
}
